<script lang="ts">
  import { site } from '$lib/config/site.config';
</script>

<div class="about-page">
  <!-- Hero -->
  <div class="about-hero">
    <div class="about-hero-badge">About Us</div>
    <h1 class="about-hero-title">Privacy is not a feature.<br/><span class="gradient-text">It's the foundation.</span></h1>
    <p class="about-hero-sub">{site.brandName} is a collection of privacy-first web applications that run entirely in your browser. No uploads, no tracking, no compromise.</p>
  </div>

  <!-- Philosophy Cards -->
  <section class="philosophy-section">
    <h2 class="section-heading">Our Philosophy</h2>
    <div class="philosophy-grid">
      <div class="phil-card">
        <div class="phil-icon">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="hsl(var(--theme-primary))" stroke-width="1.5"><path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z"/></svg>
        </div>
        <h3>Privacy First</h3>
        <p>All file processing happens locally. No uploads, no tracking cookies, no ad networks, no accounts required.</p>
      </div>
      <div class="phil-card">
        <div class="phil-icon">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="hsl(var(--theme-accent))" stroke-width="1.5"><circle cx="12" cy="12" r="10"/><path d="m9 12 2 2 4-4"/></svg>
        </div>
        <h3>Open & Transparent</h3>
        <p>Clear privacy policy, honest terms of service, and a commitment to accessibility for every user.</p>
      </div>
      <div class="phil-card">
        <div class="phil-icon">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="hsl(var(--theme-success))" stroke-width="1.5"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2M9 11a4 4 0 100-8 4 4 0 000 8zm13 0l-3 3m0 0l-3-3m3 3V4"/></svg>
        </div>
        <h3>Accessible to All</h3>
        <p>Keyboard navigation, screen reader support, high contrast colors, and fully responsive design.</p>
      </div>
    </div>
  </section>

  <!-- How it Works -->
  <section class="content-section">
    <h2 class="section-heading">How It Works</h2>
    <p>Our applications use modern web technologies to process files directly in your browser:</p>
    <div class="tech-list">
      <div class="tech-item">
        <span class="tech-label">WebAssembly</span>
        <span class="tech-desc">Performance-intensive operations like video trimming and PDF processing</span>
      </div>
      <div class="tech-item">
        <span class="tech-label">Canvas API</span>
        <span class="tech-desc">Image manipulation, drawing, and visual processing</span>
      </div>
      <div class="tech-item">
        <span class="tech-label">File API</span>
        <span class="tech-desc">Read files from your device without uploading</span>
      </div>
      <div class="tech-item">
        <span class="tech-label">localStorage</span>
        <span class="tech-desc">Save your preferences locally (optional)</span>
      </div>
    </div>
    <p>Since everything runs in your browser, you can use most tools <strong>offline</strong> after the initial page load.</p>
  </section>

  <!-- Tools -->
  <section class="content-section">
    <h2 class="section-heading">Our Tools</h2>
    <p>We offer a wide range of utilities including PDF tools (merge, fill & sign, convert), image tools (resize, compress, EXIF strip, face blur), text tools (Base64, URL encode, UUID, diff), data tools (CSV/JSON/YAML), productivity tools, and more.</p>
    <p>See the <a href="#/">home page</a> for all available tools.</p>
  </section>

  <!-- Legal -->
  <section class="content-section">
    <h2 class="section-heading">Legal</h2>
    <div class="legal-links">
      <a href="#/privacy" class="legal-link">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z"/></svg>
        Privacy Policy
      </a>
      <a href="#/terms" class="legal-link">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
        Terms of Service
      </a>
      <a href="#/accessibility" class="legal-link">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="12" cy="12" r="10"/><path d="M8 14s1.5 2 4 2 4-2 4-2M9 9h.01M15 9h.01"/></svg>
        Accessibility
      </a>
      <a href="#/feedback" class="legal-link">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M21 15a4 4 0 01-4 4H7l-4 4V7a4 4 0 014-4h10a4 4 0 014 4z"/></svg>
        Feedback
      </a>
    </div>
  </section>
</div>

<style>
  .about-page {
    max-width: 800px;
    margin: 0 auto;
    padding: 1rem 1.5rem 4rem;
  }

  /* Hero */
  .about-hero {
    text-align: center;
    padding: 3rem 0 3.5rem;
  }
  .about-hero-badge {
    display: inline-flex;
    padding: 0.3rem 0.8rem;
    border-radius: 9999px;
    font-size: 0.6875rem;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: hsl(var(--theme-primary));
    background: hsl(var(--theme-primary) / 0.1);
    border: 1px solid hsl(var(--theme-primary) / 0.2);
    margin-bottom: 1.25rem;
  }
  .about-hero-title {
    font-family: 'DM Serif Display', Georgia, serif;
    font-size: clamp(1.75rem, 4vw, 2.5rem);
    font-weight: 400;
    line-height: 1.15;
    color: hsl(var(--theme-foreground));
    margin-bottom: 1rem;
  }
  .gradient-text {
    background: linear-gradient(135deg, hsl(var(--theme-primary)), hsl(var(--theme-accent)));
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
  .about-hero-sub {
    font-size: 1rem;
    line-height: 1.65;
    color: hsl(var(--theme-muted-foreground));
    max-width: 560px;
    margin: 0 auto;
  }

  /* Philosophy */
  .philosophy-section { margin-bottom: 3rem; }
  .philosophy-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 1rem;
  }
  .phil-card {
    padding: 1.5rem;
    border-radius: var(--theme-radius-lg);
    background: hsl(var(--theme-card));
    border: 1px solid hsl(var(--theme-border) / 0.4);
    transition: all 300ms ease;
  }
  .phil-card:hover {
    transform: translateY(-3px);
    box-shadow: var(--theme-shadow-md);
    border-color: hsl(var(--theme-border));
  }
  .phil-icon {
    width: 44px; height: 44px;
    border-radius: var(--theme-radius);
    background: hsl(var(--theme-muted) / 0.15);
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 1rem;
  }
  .phil-card h3 {
    font-size: 1rem;
    font-weight: 600;
    color: hsl(var(--theme-foreground));
    margin-bottom: 0.5rem;
  }
  .phil-card p {
    font-size: 0.8125rem;
    line-height: 1.6;
    color: hsl(var(--theme-muted-foreground));
  }

  /* Content Sections */
  .content-section {
    margin-bottom: 2.5rem;
  }
  .section-heading {
    font-size: 1.25rem;
    font-weight: 600;
    color: hsl(var(--theme-foreground));
    margin-bottom: 1rem;
    padding-bottom: 0.5rem;
    border-bottom: 1px solid hsl(var(--theme-border) / 0.3);
  }
  .content-section p {
    font-size: 0.9375rem;
    line-height: 1.7;
    color: hsl(var(--theme-muted-foreground));
    margin-bottom: 0.875rem;
  }
  .content-section strong { color: hsl(var(--theme-foreground)); }
  .content-section a {
    color: hsl(var(--theme-primary));
    text-decoration: none;
    border-bottom: 1px solid hsl(var(--theme-primary) / 0.3);
    transition: border-color 200ms ease;
  }
  .content-section a:hover { border-color: hsl(var(--theme-primary)); }

  /* Tech List */
  .tech-list {
    display: flex;
    flex-direction: column;
    gap: 0.625rem;
    margin: 1rem 0 1.25rem;
  }
  .tech-item {
    display: flex;
    gap: 1rem;
    align-items: baseline;
    padding: 0.625rem 0;
    border-bottom: 1px solid hsl(var(--theme-border) / 0.15);
  }
  .tech-label {
    font-size: 0.8125rem;
    font-weight: 600;
    color: hsl(var(--theme-primary));
    min-width: 120px;
    flex-shrink: 0;
  }
  .tech-desc {
    font-size: 0.8125rem;
    color: hsl(var(--theme-muted-foreground));
    line-height: 1.5;
  }

  /* Legal Links */
  .legal-links {
    display: flex;
    flex-wrap: wrap;
    gap: 0.75rem;
  }
  .legal-link {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.625rem 1rem;
    border-radius: var(--theme-radius);
    border: 1px solid hsl(var(--theme-border));
    background: hsl(var(--theme-card) / 0.5);
    color: hsl(var(--theme-muted-foreground));
    font-size: 0.8125rem;
    font-weight: 500;
    text-decoration: none;
    transition: all 200ms ease;
  }
  .legal-link:hover {
    color: hsl(var(--theme-foreground));
    border-color: hsl(var(--theme-primary) / 0.3);
    background: hsl(var(--theme-muted) / 0.15);
  }
</style>
